// src/pages/ressources/suivi-candidature.tsx
//
// Outil gratuit "Suivi de candidatures" pour candidats.
// Sans inscription, données stockées en LocalStorage uniquement.
// Objectif SEO : générer du retour de trafic récurrent (candidat revient
// gérer son tracker = sessions répétées + signal d'autorité Google).
//
// Remplace l'ancien article zombie /ressources/articles/outil-gratuit-
// de-suivi-recherche-demploi (cf. netlify.toml 301).

import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase,
  Plus,
  Trash2,
  Edit3,
  Calendar,
  TrendingUp,
  Target,
  CheckCircle2,
  XCircle,
  Clock,
  PhoneCall,
  Users,
  Star,
  Download,
  Sparkles,
  ArrowRight,
  Save,
  X,
  Smartphone,
  Info,
  Bell,
  BellOff,
  Cloud,
  Mail,
  LogOut,
  Loader2,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';
import {
  getNotificationPermission,
  hasAskedPermission,
  requestNotificationPermission,
  notifyStaleApplications,
  sendTestNotification,
} from '../../lib/notificationsService';
import {
  sendMagicLink,
  performFullSync,
  upsertApplication,
  deleteApplication as deleteCloudApp,
  signOut as cloudSignOut,
} from '../../lib/trackedApplicationsService';
import { supabase } from '../../lib/supabase';
import type { User } from '@supabase/supabase-js';

// ─── Types ─────────────────────────────────────────────────────────────────

type Status =
  | 'wishlist'
  | 'applied'
  | 'phone'
  | 'interview'
  | 'offer'
  | 'rejected'
  | 'accepted';

interface Application {
  id: string;
  company: string;
  role: string;
  url?: string;
  status: Status;
  appliedAt: string; // ISO date
  lastUpdate: string; // ISO date
  notes?: string;
  salary?: string;
  source?: string;
  contact?: string;
  nextAction?: string;
  nextActionAt?: string; // ISO date
}

interface StatusConfig {
  label: string;
  shortLabel: string;
  color: string;
  bg: string;
  ring: string;
  Icon: typeof Briefcase;
}

const STATUS: Record<Status, StatusConfig> = {
  wishlist: {
    label: 'À candidater',
    shortLabel: 'Wishlist',
    color: 'text-neutral-600',
    bg: 'bg-neutral-100',
    ring: 'ring-neutral-200',
    Icon: Star,
  },
  applied: {
    label: 'Candidature envoyée',
    shortLabel: 'Envoyée',
    color: 'text-blue-700',
    bg: 'bg-blue-50',
    ring: 'ring-blue-200',
    Icon: Clock,
  },
  phone: {
    label: 'Échange téléphonique',
    shortLabel: 'Tel',
    color: 'text-indigo-700',
    bg: 'bg-indigo-50',
    ring: 'ring-indigo-200',
    Icon: PhoneCall,
  },
  interview: {
    label: 'Entretien(s) en cours',
    shortLabel: 'Entretien',
    color: 'text-purple-700',
    bg: 'bg-purple-50',
    ring: 'ring-purple-200',
    Icon: Users,
  },
  offer: {
    label: 'Offre reçue',
    shortLabel: 'Offre',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    ring: 'ring-amber-200',
    Icon: Target,
  },
  accepted: {
    label: 'Acceptée 🎉',
    shortLabel: 'Acceptée',
    color: 'text-green-700',
    bg: 'bg-green-50',
    ring: 'ring-green-300',
    Icon: CheckCircle2,
  },
  rejected: {
    label: 'Refusée / abandonnée',
    shortLabel: 'Refusée',
    color: 'text-neutral-500',
    bg: 'bg-neutral-50',
    ring: 'ring-neutral-200',
    Icon: XCircle,
  },
};

const STATUS_ORDER: Status[] = [
  'wishlist',
  'applied',
  'phone',
  'interview',
  'offer',
  'accepted',
  'rejected',
];

const STORAGE_KEY = 'recruteursio:suivi-candidatures:v1';

// ─── LocalStorage helpers ──────────────────────────────────────────────────

function loadApplications(): Application[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as Application[];
  } catch {
    return [];
  }
}

function saveApplications(apps: Application[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(apps));
  } catch {
    /* quota exceeded ou navigation privée — silencieux */
  }
}

function uid(): string {
  return `app_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function todayISO(): string {
  return new Date().toISOString();
}

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

function formatRelative(iso: string): string {
  const d = daysSince(iso);
  if (d === 0) return "aujourd'hui";
  if (d === 1) return 'hier';
  if (d < 7) return `il y a ${d} jours`;
  if (d < 30) return `il y a ${Math.floor(d / 7)} sem.`;
  return `il y a ${Math.floor(d / 30)} mois`;
}

// ─── Empty / sample data pour first-time visitor ───────────────────────────

const sampleApps: Application[] = [
  {
    id: 'sample_1',
    company: 'Exemple — Tesla Marseille',
    role: 'Ingénieur procédés',
    status: 'interview',
    appliedAt: new Date(Date.now() - 7 * 86400000).toISOString(),
    lastUpdate: new Date(Date.now() - 2 * 86400000).toISOString(),
    notes: 'Premier entretien manager OK. Tech interview prévu vendredi.',
    salary: '55-65 K€',
    nextAction: 'Préparer cas technique',
    nextActionAt: new Date(Date.now() + 3 * 86400000).toISOString(),
  },
];

// ─── Composant principal ────────────────────────────────────────────────────

// ─── PWA Install Prompt ────────────────────────────────────────────────────
//
// Capture l'event beforeinstallprompt (Chrome/Edge/Android) pour proposer
// l'install à un moment opportun. Safari iOS n'émet pas cet event — pour eux,
// on affiche une instruction "Partager → Sur l'écran d'accueil".

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;
  // Chrome/Edge/Firefox
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // Safari iOS
  // @ts-expect-error — non-standard mais largement supporté iOS
  if (window.navigator.standalone === true) return true;
  return false;
}

function isIOS(): boolean {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent);
}

export default function SuiviCandidaturePage() {
  const [apps, setApps] = useState<Application[]>([]);
  const [editing, setEditing] = useState<Application | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [view, setView] = useState<'kanban' | 'list'>('kanban');
  const [filterStatus, setFilterStatus] = useState<Status | 'all'>('all');

  // PWA install
  const [installPromptEvent, setInstallPromptEvent] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  // Notifications (C2)
  const [notifPermission, setNotifPermission] = useState<
    NotificationPermission | 'unsupported'
  >('default');
  const [showNotifPrompt, setShowNotifPrompt] = useState(false);

  // Cloud sync (C3)
  const [user, setUser] = useState<User | null>(null);
  const [showCloudModal, setShowCloudModal] = useState(false);
  const [magicEmail, setMagicEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<Date | null>(null);

  // Capture l'install prompt + détecte si déjà installée
  useEffect(() => {
    setIsInstalled(isStandaloneMode());

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallPromptEvent(e as BeforeInstallPromptEvent);
    };
    const onAppInstalled = () => {
      setIsInstalled(true);
      setInstallPromptEvent(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  async function handleInstall() {
    // Safari iOS : pas de prompt programmatique, on affiche les instructions
    if (isIOS() && !installPromptEvent) {
      setShowIosHint(true);
      return;
    }
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
    const { outcome } = await installPromptEvent.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setInstallPromptEvent(null);
  }

  // ─── Notifications (C2) ──────────────────────────────────────────────────

  // Détecte la permission actuelle au mount
  useEffect(() => {
    setNotifPermission(getNotificationPermission());
  }, []);

  // Demande activation notifs au moment opportun : après que l'utilisateur a
  // ajouté sa 2e candidature réelle (signal qu'il a investi dans l'outil)
  useEffect(() => {
    const real = apps.filter((a) => !a.id.startsWith('sample_'));
    if (
      real.length >= 2 &&
      notifPermission === 'default' &&
      !hasAskedPermission() &&
      !showNotifPrompt
    ) {
      setShowNotifPrompt(true);
    }
  }, [apps, notifPermission, showNotifPrompt]);

  // Check des candidatures stale au mount + toutes les heures pendant que
  // l'app est ouverte. La PWA installée garde l'app "vivante" plus longtemps
  // → notifs plus fréquentes.
  useEffect(() => {
    if (notifPermission !== 'granted') return;
    const real = apps.filter((a) => !a.id.startsWith('sample_'));
    if (real.length === 0) return;

    // Check immédiat au mount
    notifyStaleApplications(real);

    // Re-check toutes les heures
    const interval = window.setInterval(() => {
      notifyStaleApplications(real);
    }, 60 * 60 * 1000);

    return () => window.clearInterval(interval);
  }, [apps, notifPermission]);

  async function handleEnableNotifications() {
    const result = await requestNotificationPermission();
    setNotifPermission(result);
    setShowNotifPrompt(false);
    if (result === 'granted') {
      // Notif de bienvenue pour confirmer
      setTimeout(() => sendTestNotification(), 500);
      // Et check immédiatement les stale
      const real = apps.filter((a) => !a.id.startsWith('sample_'));
      setTimeout(() => notifyStaleApplications(real), 1000);
    }
  }

  // ─── Cloud sync (C3) ─────────────────────────────────────────────────

  // Détection auth + listener pour login/logout
  useEffect(() => {
    let mounted = true;

    supabase.auth.getUser().then(({ data }) => {
      if (mounted) setUser(data.user);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      setUser(session?.user ?? null);
      // Si signin frais (Magic Link confirmé) → lancer full sync
      if (event === 'SIGNED_IN' && session?.user) {
        runFullSync(session.user.id);
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runFullSync(userId: string) {
    setSyncing(true);
    const real = apps.filter((a) => !a.id.startsWith('sample_'));
    const result = await performFullSync(real, userId);
    if (result.ok) {
      setApps(result.merged);
      setLastSync(new Date());
    } else {
      console.error('[cloud sync] failed:', result.error);
    }
    setSyncing(false);
  }

  async function handleSendMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setMagicLinkLoading(true);
    setMagicLinkError(null);
    const result = await sendMagicLink(magicEmail.trim());
    setMagicLinkLoading(false);
    if (result.ok) {
      setMagicLinkSent(true);
    } else {
      setMagicLinkError(result.error ?? 'Erreur lors de l\'envoi du lien.');
    }
  }

  async function handleSignOut() {
    await cloudSignOut();
    setUser(null);
    setLastSync(null);
  }

  function openCloudModal() {
    setMagicLinkSent(false);
    setMagicLinkError(null);
    setMagicEmail('');
    setShowCloudModal(true);
  }

  // Hydratation depuis LocalStorage au mount
  useEffect(() => {
    const stored = loadApplications();
    if (stored.length === 0) {
      // Premier visiteur : pré-rempli avec 1 exemple pour montrer l'usage
      setApps(sampleApps);
    } else {
      setApps(stored);
    }
  }, []);

  // Persistence à chaque modification (sauf le sample qui ne se sauve pas)
  useEffect(() => {
    if (apps.length === 1 && apps[0].id === 'sample_1') return;
    saveApplications(apps);
  }, [apps]);

  const stats = useMemo(() => {
    const real = apps.filter((a) => !a.id.startsWith('sample_'));
    const inActive = real.filter((a) =>
      ['applied', 'phone', 'interview', 'offer'].includes(a.status),
    );
    const offers = real.filter((a) => a.status === 'offer' || a.status === 'accepted');
    const accepted = real.filter((a) => a.status === 'accepted');
    const conversion = real.length
      ? Math.round((offers.length / real.length) * 100)
      : 0;
    const oldestActive = inActive
      .map((a) => daysSince(a.lastUpdate))
      .sort((a, b) => b - a)[0];
    return {
      total: real.length,
      active: inActive.length,
      offers: offers.length,
      accepted: accepted.length,
      conversion,
      oldestActive: oldestActive ?? 0,
    };
  }, [apps]);

  const filteredApps = useMemo(() => {
    if (filterStatus === 'all') return apps;
    return apps.filter((a) => a.status === filterStatus);
  }, [apps, filterStatus]);

  function handleSave(app: Application) {
    const updated = { ...app, lastUpdate: todayISO() };
    setApps((prev) => {
      // Si on edit le sample, on le remplace par une vraie entrée
      const withoutSample = prev.filter((a) => a.id !== 'sample_1');
      const exists = withoutSample.find((a) => a.id === app.id);
      if (exists) {
        return withoutSample.map((a) => (a.id === app.id ? updated : a));
      }
      return [...withoutSample, updated];
    });
    setEditing(null);
    setIsAdding(false);
    // Sync cloud si user connecté
    if (user) {
      upsertApplication(updated, user.id).then((res) => {
        if (res.ok) setLastSync(new Date());
      });
    }
  }

  function handleDelete(id: string) {
    if (!confirm('Supprimer cette candidature ?')) return;
    setApps((prev) => prev.filter((a) => a.id !== id));
    if (user) {
      deleteCloudApp(id, user.id).then((res) => {
        if (res.ok) setLastSync(new Date());
      });
    }
  }

  function handleStatusChange(id: string, status: Status) {
    const now = todayISO();
    setApps((prev) =>
      prev.map((a) => (a.id === id ? { ...a, status, lastUpdate: now } : a)),
    );
    if (user) {
      const target = apps.find((a) => a.id === id);
      if (target) {
        upsertApplication({ ...target, status, lastUpdate: now }, user.id).then((res) => {
          if (res.ok) setLastSync(new Date());
        });
      }
    }
  }

  function exportCSV() {
    const real = apps.filter((a) => !a.id.startsWith('sample_'));
    if (real.length === 0) {
      alert('Aucune candidature à exporter.');
      return;
    }
    const header = [
      'Entreprise',
      'Poste',
      'Statut',
      'Date candidature',
      'Dernière maj',
      'Salaire',
      'Source',
      'Contact',
      'Prochaine action',
      'Notes',
    ];
    const rows = real.map((a) => [
      a.company,
      a.role,
      STATUS[a.status].label,
      new Date(a.appliedAt).toLocaleDateString('fr-FR'),
      new Date(a.lastUpdate).toLocaleDateString('fr-FR'),
      a.salary ?? '',
      a.source ?? '',
      a.contact ?? '',
      a.nextAction ?? '',
      (a.notes ?? '').replace(/[\r\n]+/g, ' '),
    ]);
    const csv = [header, ...rows]
      .map((row) =>
        row
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(';'),
      )
      .join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `suivi-candidatures-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Layout
      title="Outil gratuit de suivi de candidatures | recruteurs.io"
      description="Suivez vos candidatures en un coup d'œil : kanban des statuts, relances automatiques, export CSV, stats de conversion. 100 % gratuit, sans inscription, données privées (stockage local navigateur)."
      keywords="suivi candidatures, tracker candidature, outil candidat, recherche emploi, suivi recherche emploi"
    >
      <WebPageSchema
        page={{
          title: 'Outil gratuit de suivi de candidatures | recruteurs.io',
          description:
            "Tracker de candidatures pour candidats : kanban statuts, stats de conversion, export CSV. Gratuit, sans inscription, données locales privées.",
          url: 'https://recruteurs.io/ressources/outils/suivi-candidature',
          lastReviewed: '2026-05-18',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Ressources', url: 'https://recruteurs.io/ressources/articles' },
          { name: 'Outils', url: 'https://recruteurs.io/ressources/outils' },
          {
            name: 'Suivi de candidatures',
            url: 'https://recruteurs.io/ressources/outils/suivi-candidature',
          },
        ]}
      />

      {/* HERO */}
      <SectionBackground page="outils">
        <Section className="pt-24 pb-12">
          <div className="container-custom max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 ring-1 ring-neutral-200 shadow-sm mb-5">
                <Sparkles className="w-4 h-4 text-[#d03484]" />
                <span className="text-xs font-semibold uppercase tracking-widest text-neutral-600">
                  Outil candidat · 100 % gratuit · sans inscription
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#30475e] leading-[1.05] mb-5">
                Le tracker de candidatures qui{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                  remet de l'ordre
                </span>{' '}
                dans ta recherche d'emploi.
              </h1>
              <p className="text-lg md:text-xl text-neutral-600 leading-relaxed max-w-3xl mb-6">
                Tu candidates partout, tu oublies qui t'a répondu, tu ne sais plus à
                quoi tu en es. Cet outil tient ton tableau de bord pour toi : statuts,
                relances, stats de conversion. <strong>Tes données restent sur ton
                navigateur</strong>, on ne les voit jamais.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setIsAdding(true)}
                  className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-pink-100 hover:scale-[1.02] transition"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter une candidature
                </button>
                <button
                  onClick={exportCSV}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-[#30475e] ring-1 ring-neutral-200 hover:bg-neutral-50 transition"
                >
                  <Download className="w-4 h-4" />
                  Exporter en CSV
                </button>
                {/* Bouton Install PWA — visible si installable et pas encore installée */}
                {!isInstalled && (installPromptEvent || isIOS()) && (
                  <button
                    onClick={handleInstall}
                    className="inline-flex items-center gap-2 rounded-2xl bg-[#30475e] px-5 py-3 text-sm font-bold text-white hover:bg-[#3d5b78] transition"
                  >
                    <Smartphone className="w-4 h-4" />
                    Installer l'app
                  </button>
                )}
                {isInstalled && (
                  <div className="inline-flex items-center gap-2 rounded-2xl bg-green-50 px-4 py-3 text-sm font-semibold text-green-700 ring-1 ring-green-200">
                    <CheckCircle2 className="w-4 h-4" />
                    App installée ✓
                  </div>
                )}

                {/* Bouton Activer notifs — visible si supporté et pas encore granted */}
                {notifPermission === 'default' && (
                  <button
                    onClick={handleEnableNotifications}
                    className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-[#30475e] ring-1 ring-neutral-200 hover:bg-neutral-50 transition"
                  >
                    <Bell className="w-4 h-4" />
                    Activer les relances
                  </button>
                )}
                {notifPermission === 'granted' && (
                  <div className="inline-flex items-center gap-2 rounded-2xl bg-green-50 px-4 py-3 text-sm font-semibold text-green-700 ring-1 ring-green-200">
                    <Bell className="w-4 h-4" />
                    Relances activées
                  </div>
                )}
                {notifPermission === 'denied' && (
                  <div className="inline-flex items-center gap-2 rounded-2xl bg-neutral-100 px-4 py-3 text-sm font-semibold text-neutral-500">
                    <BellOff className="w-4 h-4" />
                    Notifications désactivées
                  </div>
                )}

                {/* Cloud sync — non connecté */}
                {!user && (
                  <button
                    onClick={openCloudModal}
                    className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-[#30475e] ring-1 ring-neutral-200 hover:bg-neutral-50 transition"
                  >
                    <Cloud className="w-4 h-4" />
                    Sauver dans mon compte
                  </button>
                )}
                {/* Cloud sync — connecté */}
                {user && (
                  <div className="inline-flex items-center gap-2 rounded-2xl bg-green-50 px-3 py-2 text-xs font-semibold text-green-700 ring-1 ring-green-200">
                    {syncing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Cloud className="w-4 h-4" />
                    )}
                    <span>{user.email}</span>
                    <button
                      onClick={handleSignOut}
                      className="ml-2 text-green-600 hover:text-green-900"
                      title="Déconnexion"
                      aria-label="Déconnexion"
                    >
                      <LogOut className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {/* Bandeau de prévention LocalStorage */}
              <div className="mt-6 rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-4 text-sm text-amber-900 flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <strong>Tes données sont stockées localement</strong> dans ton navigateur. Si tu effaces tes cookies / données de site, elles seront perdues. <strong>Pense à exporter en CSV de temps en temps</strong> pour garder une sauvegarde. Bientôt&nbsp;: sauvegarde cloud optionnelle.
                </div>
              </div>

              {/* Hint iOS Safari : pas de prompt programmatique, on guide manuellement */}
              {showIosHint && (
                <div className="mt-4 rounded-2xl bg-blue-50 ring-1 ring-blue-200 p-4 text-sm text-blue-900">
                  <p className="font-bold mb-2">Installer sur iPhone / iPad</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Appuie sur l'icône <strong>Partager</strong> 🔼 en bas de Safari</li>
                    <li>Choisis <strong>"Sur l'écran d'accueil"</strong> dans la liste</li>
                    <li>Confirme avec <strong>"Ajouter"</strong> en haut à droite</li>
                  </ol>
                  <button
                    onClick={() => setShowIosHint(false)}
                    className="mt-2 text-xs text-blue-700 underline"
                  >
                    Fermer
                  </button>
                </div>
              )}

              {/* Popup invitation notif au moment opportun (après 2e candidature) */}
              {showNotifPrompt && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 rounded-2xl bg-gradient-to-r from-[#fdf2f8] to-[#fff7ed] ring-1 ring-pink-200 p-5"
                >
                  <div className="flex items-start gap-3">
                    <Bell className="w-6 h-6 text-[#d03484] shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-bold text-[#30475e] mb-1">
                        Active les relances automatiques
                      </p>
                      <p className="text-sm text-neutral-600 mb-3">
                        On te ping quand une de tes candidatures traîne sans nouvelle depuis 10 jours. Une relance polie augmente tes chances de réponse de 30 %.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={handleEnableNotifications}
                          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-4 py-2 text-sm font-bold text-white shadow-md hover:scale-[1.02] transition"
                        >
                          <Bell className="w-4 h-4" />
                          Activer
                        </button>
                        <button
                          onClick={() => setShowNotifPrompt(false)}
                          className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-neutral-600 ring-1 ring-neutral-200 hover:bg-neutral-50"
                        >
                          Pas maintenant
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </Section>
      </SectionBackground>

      {/* STATS */}
      <Section className="py-8 border-y border-neutral-100 bg-white">
        <div className="container-custom max-w-5xl">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard label="Total" value={stats.total} Icon={Briefcase} color="text-[#30475e]" />
            <StatCard label="Actives" value={stats.active} Icon={Clock} color="text-blue-700" />
            <StatCard
              label="Offres reçues"
              value={stats.offers}
              Icon={Target}
              color="text-amber-700"
            />
            <StatCard
              label="Acceptées"
              value={stats.accepted}
              Icon={CheckCircle2}
              color="text-green-700"
            />
            <StatCard
              label="Taux conversion"
              value={`${stats.conversion}%`}
              Icon={TrendingUp}
              color="text-[#d03484]"
            />
          </div>
          {stats.oldestActive >= 14 && stats.active > 0 && (
            <div className="mt-4 rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-4 text-sm text-amber-800">
              ⏰ <strong>Conseil :</strong> ta candidature la plus ancienne en attente a{' '}
              {stats.oldestActive} jours. Une relance polie augmente tes chances de réponse de
              30 %.
            </div>
          )}
        </div>
      </Section>

      {/* TOOLBAR + VIEW SWITCH */}
      <Section className="py-6 bg-white border-b border-neutral-100">
        <div className="container-custom max-w-5xl flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setView('kanban')}
              className={`rounded-xl px-3 py-1.5 text-xs font-bold transition ${
                view === 'kanban'
                  ? 'bg-[#30475e] text-white'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              Kanban
            </button>
            <button
              onClick={() => setView('list')}
              className={`rounded-xl px-3 py-1.5 text-xs font-bold transition ${
                view === 'list'
                  ? 'bg-[#30475e] text-white'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              Liste
            </button>
          </div>
          {view === 'list' && (
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as Status | 'all')}
              className="rounded-xl bg-neutral-100 px-3 py-1.5 text-xs font-semibold text-neutral-700"
            >
              <option value="all">Tous statuts</option>
              {STATUS_ORDER.map((s) => (
                <option key={s} value={s}>
                  {STATUS[s].label}
                </option>
              ))}
            </select>
          )}
        </div>
      </Section>

      {/* KANBAN OU LISTE */}
      <Section className="py-10 bg-[#fbfaf8]">
        <div className="container-custom max-w-7xl">
          {view === 'kanban' ? (
            <KanbanView
              apps={apps}
              onEdit={setEditing}
              onDelete={handleDelete}
              onStatusChange={handleStatusChange}
            />
          ) : (
            <ListView
              apps={filteredApps}
              onEdit={setEditing}
              onDelete={handleDelete}
            />
          )}
        </div>
      </Section>

      {/* CTA RECRUTEURS.IO — LEAD CAPTURE */}
      <Section className="py-16 bg-white border-t border-neutral-100">
        <div className="container-custom max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#30475e] mb-4">
            Tu cherches un poste en{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
              PACA
            </span>{' '}
            ?
          </h2>
          <p className="text-lg text-neutral-600 mb-8 max-w-2xl mx-auto">
            On gère le recrutement de PME et ETI à Marseille, Aix-en-Provence et toute la
            région. Si l'une de nos offres te correspond, candidate — on répond sous 5 jours
            ouvrés, toujours.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="/offres"
              className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-3 text-sm font-bold text-white shadow-lg shadow-pink-100 hover:scale-[1.02] transition"
            >
              Voir nos offres en PACA
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="/candidat/decouvrir"
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-6 py-3 text-sm font-semibold text-[#30475e] ring-1 ring-neutral-200 hover:bg-neutral-50 transition"
            >
              Découvrir notre approche candidat
            </a>
          </div>
        </div>
      </Section>

      {/* MODAL ADD / EDIT */}
      <AnimatePresence>
        {(isAdding || editing) && (
          <EditModal
            initial={editing}
            onSave={handleSave}
            onCancel={() => {
              setEditing(null);
              setIsAdding(false);
            }}
          />
        )}
      </AnimatePresence>

      {/* MODAL CLOUD SYNC (Magic Link) */}
      <AnimatePresence>
        {showCloudModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
            onClick={() => setShowCloudModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center">
                    <Cloud className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-[#30475e]">
                      Sauver dans mon compte
                    </h2>
                    <p className="text-xs text-neutral-500">
                      Sync multi-device, jamais de perte
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCloudModal(false)}
                  className="text-neutral-400 hover:text-neutral-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {!magicLinkSent ? (
                <>
                  <p className="text-sm text-neutral-600 mb-4">
                    On t'envoie un <strong>lien magique par email</strong>. Tu cliques dessus, tu es connecté. Pas de mot de passe à retenir. Tes candidatures sont sauvegardées et accessibles depuis tous tes appareils.
                  </p>
                  <form onSubmit={handleSendMagicLink} className="space-y-3">
                    <div className="relative">
                      <Mail className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="email"
                        required
                        value={magicEmail}
                        onChange={(e) => setMagicEmail(e.target.value)}
                        placeholder="ton.email@exemple.fr"
                        className="w-full rounded-xl bg-neutral-50 border-0 pl-10 pr-4 py-3 text-sm"
                        disabled={magicLinkLoading}
                      />
                    </div>
                    {magicLinkError && (
                      <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
                        ⚠️ {magicLinkError}
                      </p>
                    )}
                    <button
                      type="submit"
                      disabled={magicLinkLoading || !magicEmail.includes('@')}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-4 py-3 text-sm font-bold text-white shadow-md disabled:opacity-50"
                    >
                      {magicLinkLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Mail className="w-4 h-4" />
                      )}
                      {magicLinkLoading ? 'Envoi…' : "Recevoir le lien magique"}
                    </button>
                    <p className="text-[11px] text-neutral-400 text-center">
                      En recevant le lien, tu acceptes que recruteurs.io te contacte ponctuellement avec des offres d'emploi PACA correspondant à ton profil. Tu peux te désinscrire à tout moment.
                    </p>
                  </form>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-[#30475e] mb-2">
                    Lien envoyé ✉️
                  </h3>
                  <p className="text-sm text-neutral-600 mb-4">
                    Vérifie ta boîte mail (et les spams). Clique sur le lien, tu reviens ici connecté.
                  </p>
                  <button
                    onClick={() => setShowCloudModal(false)}
                    className="text-sm text-[#d03484] font-semibold underline"
                  >
                    Fermer
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
}

// ─── Stat Card ─────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  Icon,
  color,
}: {
  label: string;
  value: number | string;
  Icon: typeof Briefcase;
  color: string;
}) {
  return (
    <div className="rounded-2xl bg-white ring-1 ring-neutral-200 p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
          {label}
        </span>
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

// ─── Kanban View ───────────────────────────────────────────────────────────

function KanbanView({
  apps,
  onEdit,
  onDelete,
  onStatusChange,
}: {
  apps: Application[];
  onEdit: (a: Application) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, s: Status) => void;
}) {
  // 3 colonnes essentielles affichées par défaut : Envoyée, Entretien, Offre
  // (les autres en bas)
  const cols: Status[] = ['applied', 'phone', 'interview', 'offer'];

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cols.map((status) => {
          const cfg = STATUS[status];
          const items = apps.filter((a) => a.status === status);
          return (
            <div key={status} className="bg-white rounded-2xl ring-1 ring-neutral-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <cfg.Icon className={`w-4 h-4 ${cfg.color}`} />
                  <h3 className={`text-sm font-bold ${cfg.color}`}>{cfg.shortLabel}</h3>
                </div>
                <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full">
                  {items.length}
                </span>
              </div>
              <div className="space-y-2 min-h-[100px]">
                {items.length === 0 ? (
                  <p className="text-xs text-neutral-400 italic text-center py-6">
                    Rien ici pour l'instant
                  </p>
                ) : (
                  items.map((app) => (
                    <KanbanCard
                      key={app.id}
                      app={app}
                      onEdit={() => onEdit(app)}
                      onDelete={() => onDelete(app.id)}
                      onStatusChange={onStatusChange}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Wishlist + Accepted + Rejected en bas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        {(['wishlist', 'accepted', 'rejected'] as Status[]).map((status) => {
          const cfg = STATUS[status];
          const items = apps.filter((a) => a.status === status);
          return (
            <div
              key={status}
              className={`${cfg.bg} rounded-2xl ring-1 ${cfg.ring} p-4`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <cfg.Icon className={`w-4 h-4 ${cfg.color}`} />
                  <h3 className={`text-sm font-bold ${cfg.color}`}>{cfg.label}</h3>
                </div>
                <span className="text-xs bg-white/70 text-neutral-600 px-2 py-0.5 rounded-full">
                  {items.length}
                </span>
              </div>
              <div className="space-y-2">
                {items.length === 0 ? (
                  <p className="text-xs text-neutral-400 italic text-center py-4">—</p>
                ) : (
                  items.map((app) => (
                    <KanbanCard
                      key={app.id}
                      app={app}
                      onEdit={() => onEdit(app)}
                      onDelete={() => onDelete(app.id)}
                      onStatusChange={onStatusChange}
                      compact
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function KanbanCard({
  app,
  onEdit,
  onDelete,
  onStatusChange,
  compact = false,
}: {
  app: Application;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (id: string, s: Status) => void;
  compact?: boolean;
}) {
  const stale = ['applied', 'phone', 'interview'].includes(app.status) && daysSince(app.lastUpdate) >= 10;
  return (
    <div
      className={`rounded-xl bg-white ring-1 ring-neutral-200 p-3 hover:shadow-md transition ${
        stale ? 'ring-amber-300' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <button
          onClick={onEdit}
          className="text-left flex-1 min-w-0"
        >
          <div className="text-sm font-bold text-[#30475e] truncate">{app.company}</div>
          <div className="text-xs text-neutral-600 truncate">{app.role}</div>
        </button>
        <button
          onClick={onDelete}
          className="text-neutral-300 hover:text-red-500 transition shrink-0"
          aria-label="Supprimer"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {!compact && (
        <>
          <div className="flex items-center gap-2 text-[11px] text-neutral-500 mb-2">
            <Calendar className="w-3 h-3" />
            <span>maj {formatRelative(app.lastUpdate)}</span>
            {stale && <span className="text-amber-600 font-semibold">· à relancer</span>}
          </div>
          <select
            value={app.status}
            onChange={(e) => onStatusChange(app.id, e.target.value as Status)}
            className="w-full text-[11px] bg-neutral-50 rounded-md py-1 px-1.5 border-0 font-semibold text-neutral-600"
          >
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                → {STATUS[s].label}
              </option>
            ))}
          </select>
        </>
      )}
    </div>
  );
}

// ─── List View ─────────────────────────────────────────────────────────────

function ListView({
  apps,
  onEdit,
  onDelete,
}: {
  apps: Application[];
  onEdit: (a: Application) => void;
  onDelete: (id: string) => void;
}) {
  if (apps.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl ring-1 ring-neutral-200">
        <Briefcase className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
        <p className="text-neutral-500">Aucune candidature pour ce filtre.</p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl ring-1 ring-neutral-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
          <tr>
            <th className="text-left py-3 px-4 font-semibold">Entreprise / Poste</th>
            <th className="text-left py-3 px-4 font-semibold">Statut</th>
            <th className="text-left py-3 px-4 font-semibold">Maj</th>
            <th className="text-right py-3 px-4 font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {apps.map((app) => {
            const cfg = STATUS[app.status];
            return (
              <tr key={app.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                <td className="py-3 px-4">
                  <div className="font-bold text-[#30475e]">{app.company}</div>
                  <div className="text-xs text-neutral-500">{app.role}</div>
                </td>
                <td className="py-3 px-4">
                  <span
                    className={`inline-flex items-center gap-1 ${cfg.bg} ${cfg.color} text-xs font-semibold rounded-full px-2 py-0.5`}
                  >
                    <cfg.Icon className="w-3 h-3" />
                    {cfg.shortLabel}
                  </span>
                </td>
                <td className="py-3 px-4 text-xs text-neutral-500">
                  {formatRelative(app.lastUpdate)}
                </td>
                <td className="py-3 px-4 text-right">
                  <button
                    onClick={() => onEdit(app)}
                    className="inline-flex items-center gap-1 text-xs text-neutral-600 hover:text-[#d03484] mr-2"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    Modifier
                  </button>
                  <button
                    onClick={() => onDelete(app.id)}
                    className="inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-red-500"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Edit Modal ────────────────────────────────────────────────────────────

function EditModal({
  initial,
  onSave,
  onCancel,
}: {
  initial: Application | null;
  onSave: (a: Application) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<Application>(
    initial ?? {
      id: uid(),
      company: '',
      role: '',
      status: 'applied',
      appliedAt: todayISO(),
      lastUpdate: todayISO(),
    },
  );

  const isEdit = !!initial;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.company.trim() || !form.role.trim()) {
      alert('Entreprise et poste sont obligatoires.');
      return;
    }
    onSave(form);
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="sticky top-0 bg-white border-b border-neutral-100 p-5 flex items-center justify-between">
            <h2 className="text-lg font-bold text-[#30475e]">
              {isEdit ? 'Modifier' : 'Nouvelle candidature'}
            </h2>
            <button
              type="button"
              onClick={onCancel}
              className="text-neutral-400 hover:text-neutral-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <Field label="Entreprise *">
              <input
                type="text"
                required
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder="Ex: Tesla Marseille"
                className="w-full rounded-xl bg-neutral-50 border-0 px-4 py-2.5 text-sm"
              />
            </Field>
            <Field label="Poste *">
              <input
                type="text"
                required
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                placeholder="Ex: Ingénieur procédés"
                className="w-full rounded-xl bg-neutral-50 border-0 px-4 py-2.5 text-sm"
              />
            </Field>
            <Field label="Statut">
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as Status })}
                className="w-full rounded-xl bg-neutral-50 border-0 px-4 py-2.5 text-sm"
              >
                {STATUS_ORDER.map((s) => (
                  <option key={s} value={s}>
                    {STATUS[s].label}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date candidature">
                <input
                  type="date"
                  value={form.appliedAt.slice(0, 10)}
                  onChange={(e) =>
                    setForm({ ...form, appliedAt: new Date(e.target.value).toISOString() })
                  }
                  className="w-full rounded-xl bg-neutral-50 border-0 px-4 py-2.5 text-sm"
                />
              </Field>
              <Field label="Salaire (optionnel)">
                <input
                  type="text"
                  value={form.salary ?? ''}
                  onChange={(e) => setForm({ ...form, salary: e.target.value })}
                  placeholder="55-65 K€"
                  className="w-full rounded-xl bg-neutral-50 border-0 px-4 py-2.5 text-sm"
                />
              </Field>
            </div>
            <Field label="Source (LinkedIn, site, contact...)">
              <input
                type="text"
                value={form.source ?? ''}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                placeholder="LinkedIn"
                className="w-full rounded-xl bg-neutral-50 border-0 px-4 py-2.5 text-sm"
              />
            </Field>
            <Field label="Contact RH ou manager">
              <input
                type="text"
                value={form.contact ?? ''}
                onChange={(e) => setForm({ ...form, contact: e.target.value })}
                placeholder="Sophie Martin (DRH)"
                className="w-full rounded-xl bg-neutral-50 border-0 px-4 py-2.5 text-sm"
              />
            </Field>
            <Field label="Prochaine action">
              <input
                type="text"
                value={form.nextAction ?? ''}
                onChange={(e) => setForm({ ...form, nextAction: e.target.value })}
                placeholder="Relancer vendredi / Préparer cas technique..."
                className="w-full rounded-xl bg-neutral-50 border-0 px-4 py-2.5 text-sm"
              />
            </Field>
            <Field label="Notes">
              <textarea
                value={form.notes ?? ''}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                placeholder="Ce que tu veux te rappeler..."
                className="w-full rounded-xl bg-neutral-50 border-0 px-4 py-2.5 text-sm"
              />
            </Field>
          </div>

          <div className="sticky bottom-0 bg-white border-t border-neutral-100 p-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-xl bg-neutral-100 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-4 py-2 text-sm font-bold text-white shadow-md"
            >
              <Save className="w-4 h-4" />
              {isEdit ? 'Enregistrer' : 'Ajouter'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-neutral-600 block mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
